import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Calendar, CheckCircle2, AlertCircle } from 'lucide-react';
import adhesionsService, { Adhesion } from '../../services/adhesions.service';
import { toast } from 'react-toastify';

const AdherentMemberships: React.FC = () => {
    const [adhesion, setAdhesion] = useState<Adhesion | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchMembership = async () => {
            try {
                const response = await adhesionsService.getMyAdhesion();
                if (response?.data) {
                    setAdhesion(response.data);
                }
            } catch (err: any) {
                console.error('Error fetching membership:', err);
                toast.error('Erreur lors de la récupération des adhésions');
            } finally {
                setLoading(false);
            }
        };

        fetchMembership();
    }, []);

    if (loading) {
        return (
            <div className="flex h-full items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary"></div>
            </div>
        );
    }

    if (!adhesion) {
        return (
            <div className="flex h-full items-center justify-center text-gray-500 min-h-[400px]">
                Aucune information d'adhésion trouvée.
            </div>
        );
    }

    // Determine Status
    const isActif = adhesion.statut === 'validated';
    const isPending = adhesion.statut === 'pending' || adhesion.statut === 'approved';
    const currentYear = new Date().getFullYear();
    const expiryDate = `31/12/${currentYear}`;

    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, staggerChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0 }
    };

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6 max-w-4xl mx-auto"
        >
            <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)] flex items-center gap-4">
                <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center">
                    <CreditCard className="w-8 h-8" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Mes Adhésions</h1>
                    <p className="text-gray-500">Consultez l'état de vos cotisations et renouvellements</p>
                </div>
            </div>

            <motion.div variants={itemVariants} className="bg-white rounded-2xl shadow-[0_4px_20px_rgb(0,0,0,0.03)] overflow-hidden">
                <div className="px-8 py-5 border-b border-gray-50/50 flex items-center justify-between">
                    <h2 className="font-semibold text-lg text-gray-800">
                        Adhésion Annuelle {currentYear}
                    </h2>
                    {isActif ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-600 border border-emerald-100/50">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Actif
                        </span>
                    ) : isPending ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-600 border border-amber-100/50">
                            <AlertCircle className="w-3 h-3 mr-1" /> En attente de paiement
                        </span>
                    ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-600 border border-red-100/50">
                            <AlertCircle className="w-3 h-3 mr-1" /> Rejeté
                        </span>
                    )}
                </div>
                
                <div className="p-8">
                    <div className="flex flex-col md:flex-row gap-8 justify-between items-start md:items-center">
                        <div className="space-y-4 flex-1">
                            <div>
                                <p className="text-sm text-gray-500 font-medium">Membre</p>
                                <p className="text-lg font-semibold text-gray-900">{adhesion.prenom} {adhesion.nom}</p>
                            </div>
                            
                            {isActif && (
                                <div>
                                    <p className="text-sm text-gray-500 font-medium">Valable jusqu'au</p>
                                    <p className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                        <Calendar className="w-5 h-5 text-gray-400" />
                                        {expiryDate}
                                    </p>
                                </div>
                            )}

                            {!isActif && isPending && (
                                <p className="text-gray-600 bg-amber-50/50 p-4 rounded-xl border border-amber-100/50 text-sm">
                                    Votre demande a été approuvée mais le paiement n'est pas encore validé. Veuillez régler votre cotisation pour activer pleinement votre compte.
                                </p>
                            )}
                        </div>

                        {/* Visual Card Representation */}
                        <div className="w-full md:w-[320px] h-[180px] rounded-2xl p-6 flex flex-col justify-between relative overflow-hidden shadow-lg 
                            bg-gradient-to-br from-secondary to-secondary-dark text-white">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -translate-y-8 translate-x-8"></div>
                            
                            <div className="flex justify-between items-start relative z-10">
                                <div>
                                    <p className="text-white/70 text-xs tracking-wider uppercase">Carte Adhérent SAPI</p>
                                    <p className="font-medium mt-1 tracking-widest bg-white/20 inline-block px-2 py-1 rounded text-sm">
                                        {adhesion.numeroMembre ? `#${adhesion.numeroMembre}` : 'EN ATTENTE'}
                                    </p>
                                </div>
                                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-md">
                                    <CreditCard className="w-5 h-5 text-white" />
                                </div>
                            </div>

                            <div className="relative z-10">
                                <p className="font-semibold text-lg">{adhesion.prenom} {adhesion.nom}</p>
                                <p className="text-white/70 text-sm mt-0.5">
                                    {isActif ? `Expire fin ${currentYear}` : 'Statut Inactif'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {isActif && (
                        <div className="mt-8 pt-8 border-t border-slate-50 flex justify-end">
                            <button className="px-6 py-2.5 bg-slate-50 hover:bg-slate-100 text-slate-700 font-medium rounded-xl transition-colors disabled:opacity-50" disabled>
                                Renouvellement non disponible
                            </button>
                        </div>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
};

export default AdherentMemberships;
